import React, { createContext, useCallback, useContext, useMemo, useRef } from 'react';
import { getFigureSasLink } from '../api';

type FigureContextType = {
  getSasUrl: (figureId: string) => Promise<string>;
  clearCache: () => void;
};

type CachedFigure = {
  url: string;
  expiryTime: string;
};

const EXPIRY_BUFFER_MS = 5 * 60 * 1000;

const isExpired = (expiryTime: string): boolean => {
  return Date.now() >= new Date(expiryTime).getTime() - EXPIRY_BUFFER_MS;
};

const FigureContext = createContext<FigureContextType | undefined>(undefined);

export const FigureProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const cacheRef = useRef<Record<string, CachedFigure>>({});
  const loadingPromisesRef = useRef<Record<string, Promise<string>>>({});

  const getSasUrl = useCallback(async (figureId: string): Promise<string> => {
    const cached = cacheRef.current[figureId];

    // If we have a cached entry and it's not expired, return it
    if (cached && !isExpired(cached.expiryTime)) {
      return cached.url;
    }

    // If we're already loading this figure, return the existing promise
    const existingPromise = loadingPromisesRef.current[figureId];
    if (existingPromise) {
      return existingPromise;
    }

    // Parse figureId which is in format "external_id-figure_id"
    const lastDashIndex = figureId.lastIndexOf('-');
    if (lastDashIndex === -1) {
      throw new Error(`Invalid figureId format: ${figureId}`);
    }

    const externalId = figureId.substring(0, lastDashIndex);
    const figureNumber = parseInt(figureId.substring(lastDashIndex + 1), 10);

    if (isNaN(figureNumber)) {
      throw new Error(`Invalid figure number in figureId: ${figureId}`);
    }

    // Create and cache the loading promise
    const loadingPromise = (async () => {
      try {
        const res = await getFigureSasLink(externalId, figureNumber);
        cacheRef.current[figureId] = { url: res.url, expiryTime: res.expiry_time };
        return res.url;
      } finally {
        delete loadingPromisesRef.current[figureId];
      }
    })();
    loadingPromisesRef.current[figureId] = loadingPromise;

    return loadingPromise;
  }, []);

  const clearCache = useCallback(() => {
    cacheRef.current = {};
    loadingPromisesRef.current = {};
  }, []);

  const value = useMemo(() => ({ getSasUrl, clearCache }), [getSasUrl, clearCache]);

  return <FigureContext.Provider value={value}>{children}</FigureContext.Provider>;
};

export const useFigureContext = () => {
  const ctx = useContext(FigureContext);
  if (!ctx) throw new Error('useFigureContext must be used inside a FigureProvider');
  return ctx;
};
